'use client'

import { MagicCard } from '@tailark/core/magicui/magic-card'
import { FeatureIconFrame } from '@tailark/core/marketing'
import { AnimatedGroup } from '@tailark/core/ui/animated-group'
import { Button } from '@tailark/core/ui/button'
import { Card, CardContent } from '@tailark/core/ui/card'
import { Input } from '@tailark/core/ui/input'
import { Blocks, Lock, Plug, Zap } from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'

const heroStyleGroupVariants = {
    container: { visible: { transition: { delayChildren: 1 } } },
    item: {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { type: 'spring' as const, bounce: 0.3, duration: 2 },
        },
    },
} as const

type FeatureItem = {
    icon: ReactNode;
    title: string;
    description: string;
};

type Context9SectionProps = {
    title?: string;
    description?: string;
    highlightPrefix?: string;
    highlightText?: string;
    highlightSuffix?: string;
    ctaLabel?: string;
    ctaHref?: string;
    features?: FeatureItem[];
};

/** Copy aligned with docs/ai-website-content (canonical journey + verified capability themes). */
const defaultFeatures: FeatureItem[] = [
    {
        icon: <Zap className="text-foreground fill-foreground/10 size-4" />,
        title: "انتشار نسخهٔ زنده",
        description:
            "پس از طراحی در ویرایشگر، نسخهٔ زنده را منتشر می‌کنید و لینک عمومی یا جاسازی را در اختیار مخاطب قرار می‌دهید.",
    },
    {
        icon: <Blocks className="text-foreground fill-foreground/10 size-4" />,
        title: "ساخت بصری فرم",
        description:
            "فیلدها، اعتبارسنجی و منطق نمایش بر اساس پاسخ‌های قبلی را تنظیم می‌کنید تا مسیر پاسخ‌گویی برای کاربر نهایی ساده و هماهنگ باشد.",
    },
    {
        icon: <Lock className="text-foreground fill-foreground/10 size-4" />,
        title: "مرزبندی داده و دسترسی",
        description:
            "هر فضای کار محدودهٔ مشخصی دارد و داده‌ها در همان بستر و در دسترس همان تیم باقی می‌ماند.",
    },
    {
        icon: <Plug className="text-foreground fill-foreground/10 size-4" />,
        title: "یکپارچه‌سازی با ابزارهای کاری",
        description:
            "در صورت نیاز، اعلان ایمیل، وب‌هوک یا اتصال به ابزارهایی مانند صفحهٔ گسترده را مطابق تنظیمات همان صفحه فعال می‌کنید.",
    },
];

export default function Context9Section({
    title = "از ایجاد صفحه تا مدیریت پاسخ، مسیر یکپارچه",
    description =
    "در لومنی صفحه را می‌سازید، منتشر می‌کنید و لینک عمومی یا جاسازی را در اختیار مخاطبان می‌گذارید. هرگاه صفحه به‌صورت فرم تعریف شده باشد، پاسخ‌ها در همان محیط جمع‌آوری می‌شود و می‌توان آن‌ها را با ایمیل، وب‌هوک یا سایر یکپارچه‌سازی‌ها به ابزارهای کاری منتقل کرد؛ این مراحل همان چیزی است که در محصول طی می‌شود.",
    highlightPrefix = "خلاصهٔ فرایند به این شکل است:",
    highlightText = "فضای کاری، صفحه، انتشار، اشتراک‌گذاری، جمع‌آوری پاسخ، و در پایان یکپارچه‌سازی",
    highlightSuffix =
    "؛ جزئیات تکمیلی را در صفحهٔ ویژگی‌ها و مستندات می‌توانید مرور کنید.",
    ctaLabel = "مشاهدهٔ ویژگی‌ها",
    ctaHref = "/features",
    features = defaultFeatures,
}: Context9SectionProps) {
    return (
        <div className="flex min-h-screen flex-col justify-center">
            <div className="mx-auto w-full max-w-5xl px-6">
                <AnimatedGroup
                    variants={heroStyleGroupVariants}
                    className="grid items-center gap-12 pb-12 md:grid-cols-2">
                    <div>
                        <div className="max-w-md">
                            <h2 className="text-foreground text-balance text-4xl font-semibold">{title}</h2>
                            <p className="my-6 text-balance text-lg">{description}</p>
                            <p className="text-muted-foreground">
                                {highlightPrefix}{' '}
                                <span className="text-title font-medium">{highlightText}</span>{' '}
                                {highlightSuffix}
                            </p>
                            <Button
                                asChild
                                className="mt-6">
                                <Link href={ctaHref}>{ctaLabel}</Link>
                            </Button>
                        </div>
                    </div>
                    <div className="relative overflow-hidden rounded-2xl bg-black p-2">
                        <div className="mask-r-from-50% absolute inset-0 items-center [background:radial-gradient(150%_115%_at_50%_5%,transparent_25%,var(--color-emerald-500)_60%,var(--color-white)_100%)]" />
                        <div className="mask-l-from-35% absolute inset-0 items-center [background:radial-gradient(150%_115%_at_50%_5%,transparent_25%,var(--color-sky-500)_60%,var(--color-white)_100%)]" />
                        <div className="relative overflow-hidden rounded-xl border border-dashed border-white/25 bg-white/10 pt-8 shadow-lg shadow-black/20">
                            <div className="absolute inset-0 bg-[radial-gradient(var(--color-white)_1px,transparent_1px)] opacity-5 [background-size:12px_12px]" />
                            <div className="absolute inset-0 translate-y-1/2 rounded-full border border-dotted bg-white/15" />
                            <div className="flex items-center justify-center px-2 pb-4">

                                <Card
                                    aria-hidden="true"
                                    className="relative z-10 !bg-card/25 backdrop-blur-sm  !ring-none !shadow-none w-full max-w-[17.5rem] space-y-2.5 rounded-3xl !p-0 !border-none"
                                >
                                    <MagicCard
                                        mode="gradient"
                                        gradientColor={"#D9D9D955"}
                                        gradientFrom={"var(--color-primary)"}
                                        gradientTo={"var(--color-secondary)"}
                                        className="p-2"
                                    >
                                        <CardContent className="!p-2 bg-card/25 flex flex-col gap-2">
                                            <div className="bg-illustration border-border/50 rounded-2xl border p-3">
                                                <div className="text-foreground/80 text-base font-semibold leading-tight">
                                                    درخواست اطلاعات
                                                </div>
                                                <div className="text-foreground/45 mt-1 text-xs">به فرم جدید من خوش آمدید</div>
                                            </div>
                                            <div className="bg-illustration border-border/50 space-y-2 rounded-2xl border p-3">
                                                <div className="space-y-1">
                                                    <div className="text-foreground/65 text-[11px] font-medium">نام و نام خانوادگی</div>
                                                    <Input className="h-6 rounded-sm" />
                                                </div>
                                                <div className="space-y-1">
                                                    <div className="text-foreground/65 text-[11px] font-medium">ایمیل کاری</div>
                                                    <Input className="h-6 rounded-sm" />
                                                </div>
                                                <div className="space-y-1">
                                                    <div className="text-foreground/65 text-[11px] font-medium">نقش سازمانی</div>
                                                    <div className="bg-background/35 text-foreground/40 flex h-6 items-center justify-between rounded-md border border-border px-2 text-[10px]">
                                                        <span>انتخاب کنید</span>
                                                        <span aria-hidden>▾</span>
                                                    </div>
                                                </div>
                                                <div className="text-foreground/50 flex items-start gap-2 border-t border-white/10 pt-2">
                                                    <div className="mt-0.5 size-3 shrink-0 rounded border border-border bg-white/20" />
                                                    <span className="text-[10px] leading-snug">
                                                        شرایط استفاده را خوانده‌ام و می‌پذیرم.
                                                    </span>
                                                </div>
                                                <div className="pt-1">
                                                    <Button className="w-full h-8">
                                                        ثبت و ارسال
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </MagicCard>
                                </Card>
                            </div>
                        </div>
                    </div>
                </AnimatedGroup>

                <AnimatedGroup
                    variants={{
                        container: {
                            visible: { transition: { staggerChildren: 0.1, delayChildren: 0.35 } },
                        },
                        item: { ...heroStyleGroupVariants.item },
                    }}
                    className="relative grid grid-cols-2 gap-x-3 gap-y-6 border-t pt-12 sm:gap-6 lg:grid-cols-4">
                    {features.map((feature) => (
                        <div
                            key={feature.title}
                            className="space-y-2">
                            <div className="flex items-center gap-2">
                                <FeatureIconFrame>{feature.icon}</FeatureIconFrame>
                                <h3 className="text-sm font-medium">{feature.title}</h3>
                            </div>
                            <p className="text-muted-foreground text-sm">{feature.description}</p>
                        </div>
                    ))}
                </AnimatedGroup>
            </div>
        </div>
    )
}